import React, { useMemo } from 'react';

import { Box } from '@nerdwallet/currency/components/Box';
import { useAnalytics } from '@lib/segment';
import Paths from '@lib/paths';

import {
  InvestmentAccountNwPlusRewardStatus,
  RewardableEventType,
} from '@generated-gql/generated-types';

import SegmentImpression, {
  ImpressionType,
} from '@components/segment-impression';

import NerdwalletPlusRewardFooterBox from '@components/modal-manager/modals/nerdwallet-plus/footer-tracker';

import RichRewardStepTile from '../reward-step-tile/rich-reward-step-tile';
import styles from '../rewards-tracker/rewards-tracker.module.scss';
import { useTrackerSteps } from '../rewards-tracker/utils';
import RewardTrackerHeader from '../reward-tracker-header';

const FUND_TREASURY_REWARD_TITLE = 'Fund your Treasury account with $5,000';
interface Props {
  investmentAccountReward: InvestmentAccountNwPlusRewardStatus;
  refetchNwPlusInvestmentAccounts?: () => void;
  isFreemiumUser?: boolean;
}

const InvestmentAccountRewardTracker: React.FC<Props> = ({
  investmentAccountReward,
  isFreemiumUser,
  refetchNwPlusInvestmentAccounts,
}) => {
  const { segmentTrack } = useAnalytics();

  const {
    selectedStep,
    setSelectedStep,
    amountOfSteps,
    amountOfCompletedSteps,
  } = useTrackerSteps(investmentAccountReward);

  const segmentTrackerProps = {
    fund_account_status:
      amountOfCompletedSteps >= 2 ? 'complete' : 'incomplete',
    leave_account_funded_status:
      amountOfCompletedSteps === 3 ? 'complete' : 'incomplete',
  };

  const handleCtaClick = () => {
    segmentTrack('NW Plus Fund Treasury Account Tracker Module CTA Clicked', {
      ...segmentTrackerProps,
    });
    refetchNwPlusInvestmentAccounts();
  };

  const goToTreasuryAccount = () => {
    segmentTrack('NW Plus Fund Treasury Account Tracker Module CTA Clicked', {
      ...segmentTrackerProps,
    });
    // The Atomic site requires a hard load of the page to initiate a new session.
    // Do not use router.push
    window.location.href = isFreemiumUser
      ? Paths.INVESTMENT_ACCOUNTS
      : Paths.NERDWALLET_PLUS_INVESTMENT_ACCOUNTS;
  };

  const rewardPoints = useMemo(
    () =>
      investmentAccountReward.rewardTypes.reduce((acc, rewardType) => {
        return acc + rewardType.points;
      }, 0),
    [investmentAccountReward],
  );

  return (
    <SegmentImpression
      eventType={ImpressionType.TRACK}
      eventName="NW Plus Fund Treasury Account Tracker Module Viewed"
      eventProps={segmentTrackerProps}
    >
      <div className={styles.container}>
        <div className={styles.container}>
          <RewardTrackerHeader
            eventType={
              investmentAccountReward.rewardTypes[0]
                .eventType as RewardableEventType
            }
            points={rewardPoints}
            showPointsForNonMembers
            amountOfSteps={amountOfSteps}
            amountOfCompletedSteps={amountOfCompletedSteps}
            headerText={FUND_TREASURY_REWARD_TITLE}
            displayCondition={
              amountOfSteps !== amountOfCompletedSteps && !!rewardPoints
            }
          />
          <Box paddingTop="3">
            {investmentAccountReward.steps?.map((step, index) => {
              const isExpanded = selectedStep === index;
              return (
                <RichRewardStepTile
                  key={step.title.text}
                  step={step}
                  isExpanded={isExpanded}
                  onSelectStep={() => {
                    setSelectedStep(isExpanded ? null : index);
                  }}
                  onCtaClick={handleCtaClick}
                />
              );
            })}
          </Box>
        </div>
        <NerdwalletPlusRewardFooterBox
          onClick={goToTreasuryAccount}
          footerText={'Go To My Treasury Account'}
          isFundingAccount
        />
      </div>
    </SegmentImpression>
  );
};

export default InvestmentAccountRewardTracker;
